import { AbilityBuilder, createMongoAbility } from '@casl/ability';

import { flattenRoutes } from '../../utils/routes';
import { APP_ROUTES } from '../../assets/MenuItems';
import { ADMIN_ROLE, GENERAL_ADMIN_ROLE } from '../../assets/AppConstants';
import { EXTRA_PERMISSIONS, MANAGE_PERMISSIONS } from '../../assets/Permissions';
import { isInternalUser } from '../../utils/internalUser';

const taskItemsFilter = ({ taskId }) => taskId;

type PermissionEntry = {
  actions?: Record<string, string[]>;
  permissions?: Record<string, (context: { workspace: any; user: any }) => boolean>;
  key: string;
  type: string;
  withView?: boolean;
  internalOnly?: boolean;
  allowedCountries?: string[];
};

export default function defineAbilityFor(workspace, user, tasks, allTasks) {
  const { can, cannot, build } = new AbilityBuilder(createMongoAbility);

  const isWorkspaceAdmin = [ADMIN_ROLE].includes(workspace?.currentUser?.role);
  const isGeneralAdmin = [GENERAL_ADMIN_ROLE].includes(user?.role);
  const isWorkspaceView = !!workspace.parentId;
  const isInternal = isInternalUser(user);

  const addPermissions = ({
    actions = {},
    permissions = {},
    key,
    type,
    withView,
    internalOnly,
    allowedCountries,
  }: PermissionEntry) => {
    const typeIsWorkspace = type === 'workspace';
    const typeIsCompany = type === 'company';

    if (allowedCountries && allowedCountries.length > 0 && !allowedCountries.includes(user?.country)) {
      return;
    }

    const isManageUser = (typeIsWorkspace && isWorkspaceAdmin) || (typeIsCompany && isGeneralAdmin);

    if (isManageUser) {
      can('manage', key);
    }

    if (internalOnly && isInternal) {
      can('view', key);
      can('enabled', key);
    }

    Object.entries(actions).forEach(([actionKey, actionUserRoles]) => {
      const inWorkspaceRole = actionUserRoles.includes(workspace?.currentUser?.role);
      const inUserRole = actionUserRoles.includes(user?.role);

      let canAction = (typeIsWorkspace && inWorkspaceRole) || (typeIsCompany && inUserRole);

      if (isWorkspaceView) {
        canAction = canAction && withView;
      }

      if (canAction) {
        can(actionKey, key);
        if (actionKey === 'view') {
          can('enabled', key);
        }
      }
    });

    Object.entries(permissions).forEach(([permissionKey, canActionFn]) => {
      const canAction = canActionFn({ workspace, user });

      if (!canAction) {
        cannot(permissionKey, key);
      }
    });
  };

  const addManagePermissions = ({ key }) => {
    can('manage', key);
  };

  const addTaskPermissions = ({ key, taskId }) => {
    const taskInTasksConfig = tasks.find(({ id }) => id === taskId);
    const enabled = !!taskInTasksConfig;

    if (enabled) {
      can('enabled', key);
    } else {
      cannot('enabled', key);
    }
  };

  const addTaskPermissionsByTaskId = () => {
    tasks.forEach(({ id }) => {
      const taskId = id.toString();
      can('enabled', taskId);
      can('view', taskId);
    });
  };

  const addAllowedPermissionsByTaskId = () => {
    allTasks.forEach(({ id, allowedCountries = [] }) => {
      const taskId = id.toString();
      if ((allowedCountries.length === 0 || allowedCountries.includes(user.country))) {
        can('allowed', taskId);
      }
    });
  };

  if (user?.role) {
    if (isGeneralAdmin) {
      can('manage', 'settings-workspaces');
    }

    const menuItems = flattenRoutes(APP_ROUTES);
    const taskItems = menuItems.filter(taskItemsFilter);

    menuItems.forEach(addPermissions);
    taskItems.forEach(addTaskPermissions);
    EXTRA_PERMISSIONS.forEach(addPermissions);
    MANAGE_PERMISSIONS.forEach(addManagePermissions);
    addTaskPermissionsByTaskId();
    addAllowedPermissionsByTaskId();
  }

  return build();
}
